import parser from 'html-react-parser';
import React, { useCallback, useEffect, useState } from 'react';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Apis from '../../../../api';
import { HEADERMENU_PATH } from '../../../../routes/RoutesURL';
import { RootState } from '../../../../store/rootReducer';
import HSButton from '../../../common/Button';
import Loader from '../../../common/Loader';
import LoginActions from '../../../login/redux/actions';
import { InvestorStatus, agreementsResign } from '../../../login/utils/Status';
import MessageActions from '../../../message/redux/actions';
import OnboardingCard from '../../onboarding/components/common/Card';
import InfoPOP from '../../onboarding/components/common/InfoPop';
import NotificationBanner from '../../onboarding/components/common/NotificationBanner';

type Props = {
    type: 'AIConsent' | 'SubscriptionAgreement' | 'HIPL AI Consent';
    showBack?: boolean;
    isResign?: boolean;
};

const DocView: React.FC<Props> = ({ type, showBack, isResign }) => {
    const user = useSelector((state: RootState) => state.user);
    const uuid = user && user.uuid;
    const investor = useSelector((state: RootState) => state.user.investor);
    const isHIPLUser = investor?.isHIPL;

    const status = investor?.status;
    const agreements = investor?.agreements;
    const AIConsent = agreements?.find((a) => a.agreementType === 'AIConsent');
    const subscriptionAgreement = agreements?.find((a) => a.agreementType === 'SubscriptionAgreement');
    const HIPLAIConsent = agreements?.find((a) => a.agreementType === 'HIPL AI Consent');

    const KYC = useSelector((state: RootState) => state.user.investor?.kyc?.reviewsV2);
    const KYC_Status = KYC?.find((k) => k.entity === 'HIPL')?.status;

    const alConsentResign = AIConsent?.shouldResign;
    const HIPLAlConsentResign = HIPLAIConsent?.shouldResign;
    const SubscriptionResign = subscriptionAgreement?.shouldResign;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isDisableAgree, setIsDisableAgree] = useState(true);
    const [htmlData, setHtmlData] = useState<string | undefined>(undefined);
    const [loader, setLoader] = useState(false);
    const [loaderAgree, setLoaderAgree] = useState(false);
    const [open, setOpen] = useState(false);
    const [agreementUUID, setAgreementUUID] = useState<string | undefined>(undefined);

    const getHTMLReq = useCallback(() => {
        if (uuid && type) {
            if (type === 'AIConsent' && ((AIConsent && !isResign) || (isResign && !alConsentResign))) {
                return;
            }
            if (
                type === 'SubscriptionAgreement' &&
                ((subscriptionAgreement && !isResign) || (isResign && !SubscriptionResign))
            ) {
                return;
            }
            if (
                type === 'HIPL AI Consent' &&
                isHIPLUser &&
                ((HIPLAIConsent && !isResign) || (isResign && !HIPLAlConsentResign))
            ) {
                return;
            }
            setLoader(true);
            Apis.fetchAgreementHtml(uuid, {
                agreementType: type,
            })
                .then(({ data }) => {
                    setHtmlData(data.html);
                    setAgreementUUID(data.agreementUUID);
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [
        uuid,
        dispatch,
        type,
        AIConsent,
        alConsentResign,
        isResign,
        subscriptionAgreement,
        SubscriptionResign,
        isHIPLUser,
        HIPLAIConsent,
        HIPLAlConsentResign,
    ]);

    useEffect(() => {
        if (
            (status?.profile !== InvestorStatus.PendingPostAIAgreements && !isResign && !isHIPLUser) ||
            (isHIPLUser &&
                HIPLAIConsent &&
                KYC_Status === InvestorStatus.Approved &&
                status?.profile === InvestorStatus.Approved &&
                !isResign)
        ) {
            navigate(HEADERMENU_PATH.deals);
        } else if (isResign && type === 'AIConsent' && !alConsentResign) {
            navigate(HEADERMENU_PATH.deals);
        } else if (isResign && type === 'SubscriptionAgreement' && !SubscriptionResign) {
            navigate(HEADERMENU_PATH.deals);
        } else if (isHIPLUser && isResign && type === 'HIPL AI Consent' && !HIPLAlConsentResign) {
            navigate(HEADERMENU_PATH.deals);
        } else {
            getHTMLReq();
        }
    }, [
        getHTMLReq,
        status,
        isResign,
        type,
        alConsentResign,
        SubscriptionResign,
        HIPLAlConsentResign,
        navigate,
        isHIPLUser,
        KYC_Status,
        HIPLAIConsent,
    ]);

    const onscroll = (event: any) => {
        const bottom = event.target.scrollHeight - event.target.scrollTop - 50 <= event.target.clientHeight;
        if (bottom) {
            setIsDisableAgree(false);
        }
    };

    const onClickIAgree = () => {
        if (uuid) {
            setLoaderAgree(true);
            Apis.signAgreement(uuid, type, agreementUUID)
                .then(({ data }) => {
                    if (
                        investor?.status.profile !== data.status?.profile &&
                        data.status?.profile === InvestorStatus.Approved
                    ) {
                        dispatch(
                            MessageActions.showMessage({
                                error: false,
                                text: 'Congratulations, you are an Approved Investor on the platform and now have full access to the platform to view details of available deals and invest in them',
                            }),
                        );
                    }
                    dispatch(
                        LoginActions.setUserData({
                            ...user,
                            currentStep: undefined,
                            investor: data,
                        }),
                    );
                    navigate(`${HEADERMENU_PATH.deals}`);
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoaderAgree(false);
                    setAgreementUUID(undefined);
                });
        }
    };

    return (
        <div className="flex-col w-full flex pt-4" id="nda1">
            {type === 'AIConsent' && alConsentResign && (
                <div className="my-3 mb-5">
                    <NotificationBanner desc={agreementsResign} />
                </div>
            )}
            {type === 'SubscriptionAgreement' && SubscriptionResign && (
                <div>
                    <NotificationBanner desc={agreementsResign} />
                </div>
            )}
            {type === 'HIPL AI Consent' &&
                HIPLAlConsentResign &&
                investor?.investorType === 'Individual Accredited' && (
                    <div>
                        <NotificationBanner desc={agreementsResign} />
                    </div>
                )}
            <InfoPOP
                open={open}
                setOpen={setOpen}
                icon={<HiOutlineLightBulb size={'1.8rem'} color="#1b3e8c" />}
                text={
                    type === 'AIConsent'
                        ? `<div>
                                <p className="text-sm WorkSans font-bold text-color-404040">AI Consent</p>
                                <p>MAS implemented legislative amendments to enhance regulatory safeguards for investors in 2018. The criteria for an investor to qualify as an Accredited Investor had been refined, and MAS introduced an opt-in regime for investors electing for Accredited Investor status. As our platform can only serve Accredited Investors, this AI consent is required to be provided from our investors.</p>
                            </div>`
                        : type === 'HIPL AI Consent'
                        ? `<div>
                                <p className="text-sm WorkSans font-bold text-color-404040">HIPL AI Consent</p>
                                <p>MAS implemented legislative amendments to enhance regulatory safeguards for investors in 2018. The criteria for an investor to qualify as an Accredited Investor had been refined, and MAS introduced an opt-in regime for investors electing for Accredited Investor status. As our platform can only serve Accredited Investors, this AI consent is required to be provided from our investors.</p>
                            </div>`
                        : ` <div>
                                <p className="text-sm WorkSans font-bold text-color-404040">Subscription agreement</p>
                                <p>The Subscription Agreement contains the general terms for the use of the platform.</p>
                            </div>`
                }
            />
            <div className="flex justify-center items-center">
                <div className="w-full flex justify-center items-center">
                    {loader && <Loader />}
                    {!loader && (
                        <div className="flex-col w-full">
                            <p className="mt-6 mb-4 w-full text-center sm:text-center md:text-left page_title WorkSans">
                                {type === 'AIConsent'
                                    ? 'AI Consent'
                                    : type === 'HIPL AI Consent'
                                    ? 'HIPL AI Consent'
                                    : 'Subscription Agreement'}
                            </p>
                            <div className="flex justify-between items-center w-full mb-2">
                                <div />
                                <div className="flex items-center cursor-pointer" onClick={() => setOpen(true)}>
                                    {' '}
                                    <HiOutlineLightBulb size={'1.25rem'} color="#1b3e8c" />{' '}
                                    <p className="right-text">Why are we requesting it?</p>
                                </div>
                            </div>
                            <OnboardingCard className="flex-col">
                                <div
                                    className="overflow-auto"
                                    onScrollCapture={onscroll}
                                    style={{ minHeight: '60vh', maxHeight: '60vh', height: 'auto' }}
                                >
                                    {htmlData && <div className="p-4 bg-white WorkSans">{parser(htmlData)}</div>}
                                </div>
                                {!loader && (
                                    <div className="flex justify-center items-center my-6 w-full mb-2">
                                        <HSButton
                                            type="primary"
                                            title="I Agree"
                                            disabled={isDisableAgree}
                                            loading={loaderAgree}
                                            onClick={onClickIAgree}
                                        />
                                        {showBack && (
                                            <HSButton
                                                title="Back"
                                                className="ml-2 rounded-none"
                                                light
                                                disabled={loaderAgree}
                                                onClick={() => navigate(`${HEADERMENU_PATH.postAgreements}`)}
                                            />
                                        )}
                                    </div>
                                )}
                            </OnboardingCard>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocView;
